import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { UNIVERSAL_CACHE_KEYS } from './cache-keys';

export interface LibraryCategory {
  id: string;
  user_id: string;
  name: string;
  content_type: string | null;
  color: string;
  icon?: string;
  created_at: string;
  updated_at: string;
}

type NewLibraryCategory = Omit<LibraryCategory, 'id' | 'user_id' | 'created_at' | 'updated_at'>;
type UpdateLibraryCategory = Partial<NewLibraryCategory>;

const getUserId = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error("User not authenticated to manage categories.");
  return user.id;
}

export const useUserLibraryCategories = () => {
  return useQuery({
    queryKey: UNIVERSAL_CACHE_KEYS.STORAGE_CATEGORIES,
    queryFn: async () => {
      const userId = await getUserId();
      const { data, error } = await supabase
        .from('universal_storage_categories')
        .select('*')
        .eq('user_id', userId)
        .order('name', { ascending: true });
      
      if (error) {
        toast.error('Failed to fetch categories.');
        throw error;
      }
      return data as LibraryCategory[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useCategoryMutations = () => {
    const queryClient = useQueryClient();

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: UNIVERSAL_CACHE_KEYS.STORAGE_CATEGORIES });
    };

    const createCategory = useMutation({
        mutationFn: async (category: NewLibraryCategory) => {
            const userId = await getUserId();
            const { data, error } = await supabase
                .from('universal_storage_categories')
                .insert([{ ...category, user_id: userId }])
                .select()
                .single();

            if (error) {
                toast.error(`Failed to create category: ${error.message}`);
                throw error;
            }
            return data;
        },
        onSuccess: () => {
            toast.success('Category created!');
            invalidateQueries();
        },
    });

    const updateCategory = useMutation({
        mutationFn: async ({ id, updates }: { id: string, updates: UpdateLibraryCategory }) => {
            const { data, error } = await supabase
                .from('universal_storage_categories')
                .update(updates)
                .eq('id', id)
                .select()
                .single();
            
            if (error) {
                toast.error(`Failed to update category: ${error.message}`);
                throw error;
            }
            return data;
        },
        onSuccess: () => {
            toast.success('Category updated!');
            invalidateQueries();
        },
    });

    const deleteCategory = useMutation({
        mutationFn: async (id: string) => {
            const { error } = await supabase
                .from('universal_storage_categories')
                .delete()
                .eq('id', id);

            if (error) {
                toast.error(`Failed to delete category: ${error.message}`);
                throw error;
            }
        },
        onSuccess: () => {
            toast.success('Category deleted.');
            invalidateQueries();
        },
    });

    return {
        createCategory: createCategory.mutateAsync,
        updateCategory: updateCategory.mutateAsync,
        deleteCategory: deleteCategory.mutateAsync,
        isPending: createCategory.isPending || updateCategory.isPending || deleteCategory.isPending,
    };
};
